// M4 world §11 — Macondo and the well-control regime (rules.WELL_CONTROL_REGIMES, SPILL_LIABILITY_M4).
//   moratorium   28 May – 12 Oct 2010, Gulf ≥ 500 ft: no spuds, drilling suspended (charters bill full day rate —
//                the MORATORIUM problem is not a standby code; own rigs keep paying crews), force majeure on charters.
//                Also any player blowout that spills > 1 MMbbl closes its region's deep water for 140 days.
//   permits      12 Oct 2010 – 1 Sept 2011: a new Gulf deepwater well needs a permit that takes U(90, 200) days.
//   costs        Drilling Safety Rule (+$400k/well, 2010 $), Well Control Rule 2016 (+$600k, 2016 $) → 2019 ($450k):
//                charged once at spud on Gulf / Alaska wells.
//   requirements containment (capping_stack_membership) for Gulf ≥ 500 ft spuds from 28 Feb 2011; SEMS (sems_program)
//                in federal waters from 15 Nov 2011; Arctic rule (Sept 2016): relief rig + capping stack.
//   blowouts     the player's own deepwater (≥ 1,000 ft) blowout: spill = rate × (1 − BOP closed) until capped
//                (capping stack 30–90 d with a membership, relief well 90–150 d without); cleanup + CWA (gross
//                negligence if a warning was ignored) + NRDA + OPA 90 economic claims (capped unless negligent).
import { allWells } from '../indexes.js';
import { int } from '../../core/rng.js';
import { dayToDate } from '../../core/time.js';
import { eqDef, fieldDef, regionDef, stat, yf, eraDollars } from '../defs.js';
import { WELL_CONTROL_REGIMES, regimesAtM4, SPILL_LIABILITY_M4, lerpAt, stepAt } from '../../data/m4/rules.js';
import { notify, spend, clamp, fmt$, log } from '../util.js';
import { fittedAt, markSpill } from '../m3/regimes.js';
import { plotWater } from '../m3/opsutil.js';
import { W, peekW, m4On, regionOf, ownWell, hashU } from './worldCore.js';

export const DEEPWATER_FT = 1000;          // "deepwater blowout" front page / liability formula threshold
export const DYNAMIC_MORATORIUM_DAYS = 140;
export const DYNAMIC_MORATORIUM_BBL = 1e6;
export const ECON_CLAIMS_PER_BBL = 10000;  // 2010 $ per spilled bbl when the OPA 90 cap is waived (Macondo ≈ $65bn total, est.)
export const CAPPING_STACK_DAYS = [30, 90];
export const RELIEF_WELL_DAYS = [90, 150];
export const BOP_PULL_DAYS = [14, 28];     // single-stack floater: pull and repair the BOP
export const DUAL_BOP_SWAP_DAYS = 2;       // 7G: run the spare stack
export const BOP_PULL_BELOW = 40;          // BOP condition (%) that forces a pull on a floater (2000+)
export const DRILLING_REGIONS = new Set(WELL_CONTROL_REGIMES.flatMap((g) => g.regionIds));
const MEMBERSHIP = 'capping_stack_membership';
const SEMS = 'sems_program';

const ACTIVE = new Set(['RIGGING_UP', 'DRILLING', 'PAUSED', 'TRIPPING', 'RUNNING_CASING', 'CASING', 'FISHING', 'STUCK', 'CEMENTING', 'SHUT_IN_KICK', 'KILLING']);

// ---- what is in force -------------------------------------------------------------------------------------------
export function regimesNow(state, regionId, waterFt = 0, day = state.day) {
  return m4On(state) && regionId ? regimesAtM4(regionId, day, waterFt) : [];
}

// Moratorium for (region, water) today: the historical one or a dynamic one after the player's own big spill.
export function moratoriumAt(state, regionId, waterFt, day = state.day) {
  if (!m4On(state) || !regionId) return null;
  const g = regimesAtM4(regionId, day, waterFt).find((x) => x.effect?.noSpud);
  if (g) return { id: g.id, from: g.from, until: g.until, minWaterFt: g.minWaterFt, source: 'regulator', forceMajeure: !!g.effect.forceMajeureAllowed };
  const d = peekW(state)?.moratoria?.[regionId];
  if (d && day >= d.from && day < d.until && waterFt >= d.minWaterFt) return { id: `moratorium_${regionId}_${d.from}`, ...d, forceMajeure: true };
  return null;
}

// Permit rule (slowdown after the moratorium): null when no permit is needed.
export function permitRule(state, regionId, waterFt) {
  return regimesNow(state, regionId, waterFt).find((g) => g.effect?.permitDays) ?? null;
}
export const permitKey = (fieldId, plot) => `${fieldId}:${plot}`;
export function permitFor(state, fieldId, plot) { return peekW(state)?.permits?.[permitKey(fieldId, plot)] ?? null; }

// File a permit (deterministic duration from the main RNG at filing). Returns the record.
export function filePermit(state, fieldId, plot, rule = null) {
  const r = rule ?? permitRule(state, regionOf(fieldId), plotWater(fieldId, plot));
  const key = permitKey(fieldId, plot);
  const Wm = W(state);
  if (Wm.permits[key]) return Wm.permits[key];
  const [lo, hi] = r?.effect?.permitDays ?? [90, 200];
  const days = int(state, lo, hi);
  Wm.permits[key] = { fieldId, plot, filedDay: state.day, readyDay: state.day + days, days, regime: r?.id ?? 'permit_slowdown' };
  return Wm.permits[key];
}

// Requirement items (containment, SEMS) still missing for a spud in (region, water).
export function missingRequirements(state, regionId, waterFt) {
  const out = [];
  for (const g of regimesNow(state, regionId, waterFt)) {
    const req = g.effect?.requires;
    if (!req || !eqDef(req)) continue;
    if (!fittedAt(state, req)) out.push({ regime: g.id, defId: req });
  }
  if (regionId === 'alaska') {
    const arctic = regimesNow(state, regionId, waterFt).find((g) => g.effect?.cappingStackOnSite);
    if (arctic && waterFt > 0 && eqDef(MEMBERSHIP) && !fittedAt(state, MEMBERSHIP) && !out.some((x) => x.defId === MEMBERSHIP)) out.push({ regime: arctic.id, defId: MEMBERSHIP });
  }
  return out;
}

// Per-well compliance cost at spud (era $): the latest cost-bearing rules in force.
export function complianceCostPerWell(state, regionId, waterFt = 0) {
  let total = 0;
  const regs = regimesNow(state, regionId, waterFt);
  const dsr = regs.find((g) => g.id === 'drilling_safety_rule_2010');
  if (dsr) total += eraDollars(state, dsr.effect.costPerWell, dsr.effect.baseYear);
  const wcr = regs.filter((g) => g.id.startsWith('well_control_rule_')).sort((a, b) => b.from - a.from)[0];
  if (wcr) total += eraDollars(state, wcr.effect.costPerWell, wcr.effect.baseYear);
  return Math.round(total);
}

// wellcontrol.onKick hook: BOP failure multiplier under the Well Control Rule (14-/21-day tests, dual shear rams).
export function m4BopFailMult(state, well) {
  if (!m4On(state) || !well) return 1;
  const regs = regimesNow(state, regionOf(well.fieldId), well.waterDepthFt ?? 0);
  let m = 1;
  for (const g of regs) if (g.effect?.bopFailMult) m *= g.effect.bopFailMult;
  return m;
}

// m3 regimeIncidentMult hook: SEMS ×0.85 in the Gulf once required and in place (cheap: date + region first).
const semsCache = new WeakMap();
export function m4IncidentMult(state, regionId) {
  if (regionId !== 'gulf_of_mexico' || !m4On(state)) return 1;
  const c = semsCache.get(state);
  if (c && c.day === state.day) return c.m;
  const g = regimesAtM4(regionId, state.day, 0).find((x) => x.effect?.requires === SEMS);
  const m = g && fittedAt(state, SEMS) ? g.effect.incidentMult ?? 1 : 1;
  semsCache.set(state, { day: state.day, m });
  return m;
}

// ---- spud gate (opsactions.preAssign → worldDeep.worldPreAssign) ---------------------------------------------------
// Returns a refusal string or null. `fresh` = a new well (no well on the plot yet).
export function regimeSpudBlock(state, fieldId, plot, water, fresh) {
  const rid = regionOf(fieldId);
  if (!DRILLING_REGIONS.has(rid) && !peekW(state)?.moratoria?.[rid]) return null;
  const mor = moratoriumAt(state, rid, water);
  if (mor) return `Deepwater moratorium in ${regionDef(rid)?.name ?? rid}: no wells in ${mor.minWaterFt}+ ft of water until ${dayStr(mor.until)}.`;
  if (!fresh) return null;
  const miss = missingRequirements(state, rid, water);
  if (miss.length) return `The regulator will not approve the permit without ${miss.map((m) => eqDef(m.defId)?.name ?? m.defId).join(' and ')}.`;
  const rule = permitRule(state, rid, water);
  if (rule) {
    const p = permitFor(state, fieldId, plot) ?? filePermit(state, fieldId, plot, rule);
    if (state.day < p.readyDay) return `Drilling permit filed with the regulator on ${dayStr(p.filedDay)} — approval expected ${dayStr(p.readyDay)} (${p.readyDay - state.day} days).`;
  }
  return null;
}
const dayStr = (d) => { if (d == null) return 'further notice'; const { year, month, dom } = dayToDate(d); return `${dom} ${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month]} ${year}`; };

// ---- daily (tickPost / tickEcon) ------------------------------------------------------------------------------------
// Compliance cost at spud, once per well.
export function tickSpudCosts(state) {
  for (const w of allWells(state)) {
    if (w.spudDay == null || w.m4reg != null || !ownWell(w)) continue;
    const rid = regionOf(w.fieldId);
    if (!DRILLING_REGIONS.has(rid) || w.spudDay < state.day - 1) { w.m4reg = 0; continue; }
    const cost = complianceCostPerWell(state, rid, w.waterDepthFt ?? 0);
    w.m4reg = cost;
    if (cost > 0) { spend(state, cost, 'other', false); w.cost = (w.cost ?? 0) + cost; }
  }
}

// Moratorium notices (the front page goes to players with Gulf deepwater rigs that day) and the bus event.
export function tickMoratoriumNotices(state) {
  const Wm = W(state);
  for (const rid of DRILLING_REGIONS) {
    const mor = moratoriumAt(state, rid, 10000);
    const key = `mor:${rid}`;
    const was = Wm.regimesSeen[key] ?? null;
    if (mor && was !== mor.id) {
      Wm.regimesSeen[key] = mor.id;
      const exposed = exposedDeepRigs(state, rid, mor.minWaterFt);
      notify(state, 'moratorium', { regionId: rid, active: true, id: mor.id, untilDay: mor.until, rigs: exposed.length, text: `Deepwater moratorium: ${regionDef(rid)?.name ?? rid}, ${mor.minWaterFt}+ ft.` });
      if (exposed.length) notify(state, 'news', { kind: 'politics', regionId: rid, press: 'moratorium', pause: true, level: 'major', fieldId: exposed[0].fieldId,
        text: `Moratorium: drilling suspended on ${exposed.length} of your deepwater well${exposed.length > 1 ? 's' : ''} in ${regionDef(rid)?.name ?? rid} until ${dayStr(mor.until)}. Rigs on standby still bill; chartered rigs may be released under force majeure.` });
    } else if (!mor && was) {
      Wm.regimesSeen[key] = null;
      notify(state, 'moratorium', { regionId: rid, active: false, text: `The deepwater moratorium in ${regionDef(rid)?.name ?? rid} is lifted.` });
    }
  }
  // regime start notices for regions where the player operates
  for (const g of WELL_CONTROL_REGIMES) {
    if (state.day < g.from || (g.until != null && state.day >= g.until) || Wm.regimesSeen[g.id] != null) continue;
    Wm.regimesSeen[g.id] = state.day;
    if (g.effect?.noSpud) continue;
    if (!allWells(state).some((w) => ownWell(w) && g.regionIds.includes(regionOf(w.fieldId)))) continue;
    log(state, `New offshore rule in force: ${g.id.replace(/_/g, ' ')}.`, 'info');
  }
}
function exposedDeepRigs(state, rid, minWater) {
  return allWells(state).filter((w) => ownWell(w) && ACTIVE.has(w.status) && (w.rigId || w.contract) && (w.waterDepthFt ?? 0) >= minWater && regionOf(w.fieldId) === rid);
}

// Monthly fees: containment consortium, SEMS programme.
export function chargeRegimeFees(state) {
  for (const id of [MEMBERSHIP, SEMS]) {
    const d = eqDef(id);
    if (!d || !fittedAt(state, id)) continue;
    const fee = eraDollars(state, stat(d, 'annualFee', 0), stat(d, 'baseYear', 2012)) / 12;
    if (fee > 0) spend(state, fee, 'insurance', false);
  }
}

// ---- the player's deepwater blowout --------------------------------------------------------------------------------
export function spillCostPerBbl(state, { negligent = false, y = Math.min(yf(state), 2025) } = {}) {
  const L = SPILL_LIABILITY_M4;
  const base = (x) => eraDollars(state, x, L.baseYear);
  const cleanup = base(L.cleanupPerBbl.offshore);
  const cwa = negligent ? lerpAt(L.cwaGrossNegligencePerBbl, y) : lerpAt(L.cwaPerBbl, y);
  const nrda = base(L.nrdaPerBbl);
  return { cleanup, cwa, nrda, perBbl: cleanup + cwa + nrda };
}
// OPA 90 economic claims for a spill of `bbl` (capped unless negligent).
export function economicClaims(state, bbl, negligent) {
  const raw = eraDollars(state, ECON_CLAIMS_PER_BBL, SPILL_LIABILITY_M4.baseYear) * bbl;
  if (negligent) return raw;
  const cap = (stepAt(SPILL_LIABILITY_M4.opa90EconomicCapM, Math.min(yf(state), 2025)) ?? 75) * 1e6;
  return Math.min(raw, cap);
}
// Pure estimate (view + tests): the bill for `bbl` spilled.
export function blowoutLiability(state, bbl, negligent = false) {
  const c = spillCostPerBbl(state, { negligent });
  const claims = economicClaims(state, bbl, negligent);
  return { ...c, bbl, claims, total: c.perBbl * bbl + claims, rep: -SPILL_LIABILITY_M4.reputationPerKbbl * bbl / 1000 };
}

const bopOf = (state, rig) => (rig?.bopUid && state.items[rig.bopUid] ? { item: state.items[rig.bopUid], def: eqDef(state.items[rig.bopUid].defId) } : null);

function startRecord(state, w) {
  const rig = w.rigId ? state.rigs[w.rigId] : null;
  const bop = bopOf(state, rig);
  const rid = regionOf(w.fieldId);
  const u = hashU(state, `bop:${w.id}:${w.blowout?.day ?? state.day}`);
  const closed = bop ? clamp((0.15 + 0.45 * u) * (stat(bop.def, 'dualShear', false) ? 1.5 : 1), 0, 0.95) : 0;
  const membership = fittedAt(state, MEMBERSHIP);
  const [lo, hi] = membership ? CAPPING_STACK_DAYS : RELIEF_WELL_DAYS;
  const capDays = lo + Math.floor(hashU(state, `cap:${w.id}:${state.day}`) * (hi - lo + 1));
  const warnings = [];
  if (bop && (bop.item.condition ?? 100) < 60) warnings.push('BOP overdue for testing');
  if (missingRequirements(state, rid, w.waterDepthFt ?? 0).length) warnings.push('required containment / SEMS missing');
  if (w.m4warningIgnored) warnings.push(w.m4warningIgnored);
  const rate = Math.max(0, w.blowout?.rate ?? 0);
  const rec = { wellId: w.id, fieldId: w.fieldId, regionId: rid, startDay: state.day, rate: Math.round(rate), bopClosed: Math.round(closed * 100) / 100,
    spillRate: Math.round(rate * (1 - closed)), capDays, method: membership ? 'capping_stack' : 'relief_well', negligent: warnings.length > 0, warnings,
    spilled: 0, charged: 0, open: true, closedDay: null, claims: 0, waterDepthFt: w.waterDepthFt ?? 0 };
  // the wild well stays wild until the stack lands / the relief well intersects
  const t = w.timerId ? state.timers[w.timerId] : null;
  if (t && t.kind === 'CAPPING') { t.daysLeft = Math.max(t.daysLeft, capDays); t.totalDays = Math.max(t.totalDays, capDays); }
  return rec;
}

export function tickDeepwaterBlowouts(state) {
  const Wm = W(state);
  for (const w of allWells(state)) {
    if (w.status !== 'BLOWOUT' || !w.blowout || !ownWell(w) || !(w.offshore || (w.waterDepthFt ?? 0) > 0) || (w.waterDepthFt ?? 0) < DEEPWATER_FT) continue;
    const prev = Wm.blowouts[w.id];
    if (prev?.open) continue;
    const rec = startRecord(state, w);
    Wm.blowouts[w.id] = rec;
    state.company.stats.deepwaterBlowouts = (state.company.stats.deepwaterBlowouts ?? 0) + 1;
    const rig = w.rigId ? state.rigs[w.rigId] : null;
    notify(state, 'accident', { press: 'deepwaterBlowout', incidentType: 'blowout', wellId: w.id, fieldId: w.fieldId, rigUid: rig?.uid ?? null, deaths: 0, pause: true, level: 'major',
      text: `${w.name}: blowout in ${Math.round(rec.waterDepthFt).toLocaleString('en-US')} ft of water. The BOP closed ${Math.round(rec.bopClosed * 100)}% — about ${rec.spillRate.toLocaleString('en-US')} bbl/d is reaching the sea. ${rec.method === 'capping_stack' ? 'The containment consortium is mobilizing a capping stack' : 'Only a relief well can kill it'} (~${rec.capDays} days).${rec.negligent ? ' Investigators already ask why warnings were ignored.' : ''}` });
  }
  for (const rec of Object.values(Wm.blowouts)) {
    if (!rec.open) continue;
    const w = state.wells[rec.wellId];
    const flowing = w && w.status === 'BLOWOUT' && w.blowout;
    if (flowing) {
      const bbl = rec.spillRate;
      if (bbl > 0) chargeDay(state, rec, bbl);
      continue;
    }
    closeRecord(state, rec);
  }
}

function chargeDay(state, rec, bbl) {
  const c = spillCostPerBbl(state, { negligent: rec.negligent });
  const cost = c.perBbl * bbl;
  spend(state, c.cleanup * bbl, 'spills', false);
  spend(state, (c.cwa + c.nrda) * bbl, 'fines', false);
  rec.spilled += bbl; rec.charged += cost;
  state.company.reputation = clamp(state.company.reputation - SPILL_LIABILITY_M4.reputationPerKbbl * bbl / 1000, 0, 100);
  state.company.stats.bblSpilled = (state.company.stats.bblSpilled ?? 0) + bbl;
  markSpill(state, rec.fieldId, bbl, 'deepwater blowout', { wellId: rec.wellId, ours: true });
  if (rec.spilled > DYNAMIC_MORATORIUM_BBL && !rec.moratorium) {
    rec.moratorium = true;
    const Wm = W(state);
    Wm.moratoria[rec.regionId] = { from: state.day, until: state.day + DYNAMIC_MORATORIUM_DAYS, minWaterFt: 500, source: 'player_spill', wellId: rec.wellId };
    notify(state, 'news', { kind: 'politics', regionId: rec.regionId, pause: true, level: 'major', text: `Your spill passes a million barrels: the government halts all deepwater drilling in ${regionDef(rec.regionId)?.name ?? rec.regionId} for ${DYNAMIC_MORATORIUM_DAYS} days.` });
  }
}

function closeRecord(state, rec) {
  rec.open = false;
  rec.closedDay = state.day;
  rec.claims = Math.round(economicClaims(state, rec.spilled, rec.negligent));
  if (rec.claims > 0) spend(state, rec.claims, 'fines', false);
  rec.charged += rec.claims;
  state.company.stats.spillCosts = (state.company.stats.spillCosts ?? 0) + rec.charged;
  notify(state, 'spill', { fieldId: rec.fieldId, wellId: rec.wellId, bbl: Math.round(rec.spilled), cost: rec.charged, cause: 'a deepwater blowout', level: 'major', pause: true,
    text: `${fieldDef(rec.fieldId)?.name}: the wild well is dead after ${state.day - rec.startDay} days. ${Math.round(rec.spilled).toLocaleString('en-US')} bbl spilled; cleanup, Clean Water Act${rec.negligent ? ' (gross negligence)' : ''} penalties, damages and claims total ${fmt$(rec.charged)}.` });
}

// ---- views ------------------------------------------------------------------------------------------------------------
export function regimeStatusM4(state, regionId = 'gulf_of_mexico', waterFt = 5000) {
  const on = m4On(state);
  const regs = on ? regimesAtM4(regionId, state.day, waterFt) : [];
  const mor = moratoriumAt(state, regionId, waterFt);
  const permit = on ? permitRule(state, regionId, waterFt) : null;
  const Wm = peekW(state);
  return {
    regionId, waterFt, active: on,
    regimes: regs.map((g) => ({ id: g.id, from: g.from, until: g.until ?? null, effect: { ...g.effect } })),
    moratorium: mor ? { id: mor.id, untilDay: mor.until ?? null, source: mor.source, minWaterFt: mor.minWaterFt, forceMajeure: !!mor.forceMajeure } : null,
    permitDays: permit?.effect?.permitDays ?? null,
    permits: Object.values(Wm?.permits ?? {}).filter((p) => regionOf(p.fieldId) === regionId).map((p) => ({ ...p, ready: state.day >= p.readyDay, daysLeft: Math.max(0, p.readyDay - state.day) })),
    missing: on ? missingRequirements(state, regionId, waterFt).map((m) => ({ ...m, name: eqDef(m.defId)?.name ?? m.defId })) : [],
    costPerWell: on ? complianceCostPerWell(state, regionId, waterFt) : 0,
    bopFailMult: on ? regs.reduce((m, g) => m * (g.effect?.bopFailMult ?? 1), 1) : 1,
    sems: on ? fittedAt(state, SEMS) : false, containment: on ? fittedAt(state, MEMBERSHIP) : false,
    blowouts: Object.values(Wm?.blowouts ?? {}).filter((b) => b.regionId === regionId).map((b) => ({ ...b, warnings: [...b.warnings] })),
    liabilityPer1kBbl: on ? Math.round(blowoutLiability(state, 1000).total) : 0,
  };
}
